import { DEFAULT_MODEL } from '../constants/ModelOptions';
import { ModelName, MODEL_NAMES } from '../constants/modelNames';
import { uiLogger } from './loggerInstance';

const CANONICAL_MODEL_NAMES = new Set<string>(Object.values(ModelName));

const MODEL_ALIAS_BY_LOWER: Record<string, ModelName> = {
  openai: ModelName.GPT,
  gpt: ModelName.GPT,
  'gpt-4': ModelName.GPT,
  'gpt-4o': ModelName.GPT,
  anthropic: ModelName.CLAUDE,
  claude: ModelName.CLAUDE,
  gemini: ModelName.GEMINI,
  google: ModelName.GEMINI,
  cohere: ModelName.COHERE,
  xai: ModelName.GROK,
  grok: ModelName.GROK,
  mistral: ModelName.MISTRAL,
  deepseek: ModelName.DEEPSEEK,
  nvidia: ModelName.NVIDIA,
  alibaba: ModelName.ALIBABA,
  ai21: ModelName.AI21,
  groq: ModelName.GROQ,
  together: ModelName.TOGETHER,
  perplexity: ModelName.PERPLEXITY,
  huggingface: ModelName.HUGGINGFACE,
  'hugging face': ModelName.HUGGINGFACE,
};

const MODEL_NAME_SUBSTRINGS: Array<[string, ModelName]> = [
  ['recommended', ModelName.RECOMMENDED],
  ['claude', ModelName.CLAUDE],
  ['gpt', ModelName.GPT],
  ['gemini', ModelName.GEMINI],
  ['cohere', ModelName.COHERE],
  ['grok', ModelName.GROK],
];

const MODEL_NAME_BY_API_PROVIDER_KEY: Record<string, ModelName> = {
  RECOMMENDED: MODEL_NAMES.RECOMMENDED,
  OPENAI: MODEL_NAMES.OPENAI,
  ANTHROPIC: MODEL_NAMES.ANTHROPIC,
  GEMINI: MODEL_NAMES.GEMINI,
  COHERE: MODEL_NAMES.COHERE,
  XAI: MODEL_NAMES.XAI,
  MISTRAL: MODEL_NAMES.MISTRAL,
  DEEPSEEK: MODEL_NAMES.DEEPSEEK,
  NVIDIA: MODEL_NAMES.NVIDIA,
  ALIBABA: MODEL_NAMES.ALIBABA,
  AI21: MODEL_NAMES.AI21,
  GROQ: MODEL_NAMES.GROQ,
  TOGETHER: MODEL_NAMES.TOGETHER,
  PERPLEXITY: MODEL_NAMES.PERPLEXITY,
  HUGGINGFACE: MODEL_NAMES.HUGGINGFACE,
};

function unwrapJsonEncodedModelString(s: string): string {
  const t = s.trim();
  if (t.length < 2 || !t.startsWith('"') || !t.endsWith('"')) return t;
  try {
    const parsed = JSON.parse(t) as unknown;
    return typeof parsed === 'string' ? parsed.trim() : t;
  } catch {
    return t;
  }
}

/**
 * Map API/runtime model strings (and legacy labels) to a valid {@link ModelName}.
 * Use wherever values may not be trusted enum members (sessions, profile, processing state).
 */
export function resolveToModelName(raw: unknown): ModelName {
  if (typeof raw !== 'string') {
    return DEFAULT_MODEL;
  }

  let s = raw;
  for (let i = 0; i < 2; i++) {
    const next = unwrapJsonEncodedModelString(s);
    if (next === s) break;
    s = next;
  }
  s = s.trim();
  if (!s) {
    return DEFAULT_MODEL;
  }

  if (CANONICAL_MODEL_NAMES.has(s)) {
    return s as ModelName;
  }

  const lower = s.toLowerCase();
  const fromProviderKey = MODEL_NAME_BY_API_PROVIDER_KEY[s.toUpperCase()];
  if (fromProviderKey) {
    return fromProviderKey;
  }

  const aliased = MODEL_ALIAS_BY_LOWER[lower];
  if (aliased) {
    return aliased;
  }

  for (const v of CANONICAL_MODEL_NAMES) {
    if (v.toLowerCase() === lower) {
      return v as ModelName;
    }
  }

  for (const [needle, model] of MODEL_NAME_SUBSTRINGS) {
    if (lower.includes(needle)) {
      return model;
    }
  }

  uiLogger.warn('Unknown model value; using default model', { raw });
  return DEFAULT_MODEL;
}
